import { ChangeEvent, FormEvent, MouseEvent, useEffect, useState } from "react";

import { InfiniteDataType, toDateString } from "~/libs";

import {
  useDateRange,
  useInput,
  useApisMembersInsightPostsIndex,
  usePostSortType,
  useArray,
  useApisMembersInsightPostSearchItemsIndex,
  useProvidersEmployeeLayout,
  useCompleteStatusDropdown,
  useMultiJobCategoryMultiJobCategoryPhaseDropdown,
} from "~/hooks";

import { RangeDatePropsType } from "~/components/molecules";

import {
  ApiMembersInsightPostsIndexRequestType,
  InsightPostType,
  ApiMemberInsightPostsIndexResponseType,
  OptionType,
  SortType,
  SingleValueType,
  ChildType,
  ParentType,
} from "~/domains";

const today = new Date();

type ReturnType = {
  insightPosts: InsightPostType[];
  data?: InfiniteDataType<ApiMemberInsightPostsIndexResponseType>;
  hasNextPage?: boolean;
  isLoading: boolean;
  keyword: string;
  selectableDropdownHasCommentIds: OptionType[];
  selectedDropdownHasCommentId?: SingleValueType<OptionType>;
  selectableDropdownHasBookmarkIds: OptionType[];
  selectedDropdownHasBookmarkId?: SingleValueType<OptionType>;
  selectableDropdownSharedIds: OptionType[];
  selectedDropdownSharedId?: SingleValueType<OptionType>;
  startDate: Date | null;
  endDate: Date | null;
  sort: SortType;
  selectableJobCategories: ParentType[];
  selectedJobCategories: ParentType[];
  optionSelectableJobCategoryPhases: ChildType[];
  optionSelectedJobCategoryPhases: ChildType[];
  handleDateRangeWithSearch: (startDate: Date, endDate: Date) => void;
  onChangeDateRange: (value: RangeDatePropsType) => void;
  onChangeJobCategoryPhases: (options: ChildType[]) => void;
  onChangeJobCategories: (options: ParentType[]) => void;
  deleteIndexInsightPost: (index: number) => void;
  updateIndexInsightPost: (newItem: InsightPostType, index: number) => void;
  addInsightPost: (newItem: InsightPostType) => void;
  setSort: (val: SortType) => void;
  fetchNextPage: () => void;
  refetch: () => void;
  onChangeKeyword: (e: ChangeEvent<HTMLInputElement>) => void;
  onSearchSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onChangeDropdownHasCommentId: (value: SingleValueType<OptionType>) => void;
  onChangeDropdownHasBookmarkId: (value: SingleValueType<OptionType>) => void;
  onChangeDropdownSharedId: (value: SingleValueType<OptionType>) => void;
  onConditionReset: (e: MouseEvent<HTMLButtonElement>) => void;
};

export const useSearchMembersInsightPosts = (): ReturnType => {
  const { setFalseRightSidebarMain } = useProvidersEmployeeLayout();
  const { items, setItems, deleteIndexItem, updateIndexItem, unshiftItem } =
    useArray<InsightPostType>([]);

  const { data: fetchData } = useApisMembersInsightPostSearchItemsIndex();

  const {
    selectableJobCategories,
    selectedJobCategories,
    onChangeJobCategories,
    optionSelectableJobCategoryPhases,
    optionSelectedJobCategoryPhases,
    onChangeJobCategoryPhases,
  } = useMultiJobCategoryMultiJobCategoryPhaseDropdown({
    selectableJobCategories: fetchData?.jobCategories,
    selectableJobCategoryPhases: fetchData?.jobCategoryPhases,
    withAllJobCategoryPhase: true,
  });

  const [{ value: keyword, onChange: onChangeKeyword }, setKeyword] =
    useInput("");
  const [
    {
      statusValue: hasComment,
      selectedDropdownCompleteStatusId: selectedDropdownHasCommentId,
      selectableDropdownCompleteStatusIds: selectableDropdownHasCommentIds,
      onChangeDropdownCompleteStatusId: onChangeDropdownHasCommentId,
    },
  ] = useCompleteStatusDropdown({
    initialStatusNames: {
      true: "リプライあり",
      false: "リプライなし",
    },
  });
  const [
    {
      statusValue: hasBookmark,
      selectedDropdownCompleteStatusId: selectedDropdownHasBookmarkId,
      selectableDropdownCompleteStatusIds: selectableDropdownHasBookmarkIds,
      onChangeDropdownCompleteStatusId: onChangeDropdownHasBookmarkId,
    },
  ] = useCompleteStatusDropdown({
    initialStatusNames: {
      true: "ブックマークあり",
      false: "ブックマークなし",
    },
  });
  const [
    {
      statusValue: shared,
      selectedDropdownCompleteStatusId: selectedDropdownSharedId,
      selectableDropdownCompleteStatusIds: selectableDropdownSharedIds,
      onChangeDropdownCompleteStatusId: onChangeDropdownSharedId,
    },
  ] = useCompleteStatusDropdown({
    initialStatusNames: {
      true: "シェアあり",
      false: "シェアなし",
    },
  });
  const [{ startDate, endDate, onChange: onChangeDateRange }] = useDateRange([
    today,
    today,
  ]);
  const { sort, snakeKeyValue, setSort } = usePostSortType();

  const [params, setParams] = useState<ApiMembersInsightPostsIndexRequestType>({
    postDateFrom: startDate ? toDateString(startDate) : "",
    postDateTo: endDate ? toDateString(endDate) : "",
    sortType: snakeKeyValue,
  });

  const { data, fetchNextPage, hasNextPage, refetch, isFetching } =
    useApisMembersInsightPostsIndex({
      params,
      config: {
        onSuccess: (data) => {
          setItems(data.pages.map((page) => page.insightPosts).flat());
        },
      },
    });

  const onSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setParams({
      keyword,
      hasComment,
      hasBookmark,
      shared,
      sortType: snakeKeyValue,
      jobCategoryIds: selectedJobCategories.map((ele) => ele.id),
      jobCategoryPhaseIds: optionSelectedJobCategoryPhases?.map(
        (ele) => ele.id,
      ),
      postDateFrom: startDate ? toDateString(startDate) : "",
      postDateTo: endDate ? toDateString(endDate) : "",
    });
    setFalseRightSidebarMain();
  };

  const onConditionReset = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const today = new Date();
    setKeyword("");
    onChangeDropdownHasBookmarkId(null);
    onChangeDropdownHasCommentId(null);
    onChangeDropdownSharedId(null);
    onChangeDateRange([today, today]);
    onChangeJobCategories([]);
    onChangeJobCategoryPhases([]);
    setSort("createdAtDesc");
    setParams({
      postDateFrom: toDateString(today),
      postDateTo: toDateString(today),
      sortType: snakeKeyValue,
    });
    setFalseRightSidebarMain();
  };

  useEffect(() => {
    setParams((prevParams) => ({
      ...prevParams,
      sortType: snakeKeyValue,
    }));
  }, [snakeKeyValue]);

  const handleDateRangeWithSearch = (from: Date, to: Date) => {
    onChangeDateRange([from, to]);

    setParams((prevParams) => ({
      ...prevParams,
      postDateFrom: from ? toDateString(from) : "",
      postDateTo: to ? toDateString(to) : "",
    }));
  };

  return {
    insightPosts: items,
    data,
    isLoading: isFetching,
    hasNextPage,
    keyword,
    selectableDropdownHasCommentIds,
    selectedDropdownHasCommentId,
    selectableDropdownHasBookmarkIds,
    selectedDropdownHasBookmarkId,
    selectableDropdownSharedIds,
    selectedDropdownSharedId,
    startDate,
    endDate,
    sort,
    selectableJobCategories,
    selectedJobCategories,
    optionSelectableJobCategoryPhases,
    optionSelectedJobCategoryPhases,
    addInsightPost: unshiftItem,
    updateIndexInsightPost: updateIndexItem,
    deleteIndexInsightPost: deleteIndexItem,
    handleDateRangeWithSearch,
    onChangeDateRange,
    onChangeJobCategoryPhases,
    onChangeJobCategories,
    setSort,
    fetchNextPage,
    refetch,
    onChangeKeyword,
    onSearchSubmit,
    onChangeDropdownHasCommentId,
    onChangeDropdownHasBookmarkId,
    onChangeDropdownSharedId,
    onConditionReset,
  };
};
